import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL, BUSINESS, NAV_LINKS } from "../constants";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <img
            src={LOGO_URL}
            alt="Néma Services"
            className="h-12 w-12 object-contain"
            loading="eager"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-semibold text-[#0F2B5B] text-lg tracking-tight">
              NÉMA
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#2E9E6E] font-medium">
              Services
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-slate-700 hover:text-[#0F2B5B] transition-colors relative group"
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2E9E6E] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="hidden md:flex items-center gap-2 text-sm font-medium text-[#0F2B5B] hover:text-[#2E9E6E] transition-colors"
            data-testid="navbar-phone"
          >
            <Phone className="w-4 h-4" strokeWidth={1.75} />
            {BUSINESS.phone}
          </a>
          <a
            href="#devis"
            className="hidden md:inline-flex items-center gap-2 bg-[#0F2B5B] hover:bg-[#153a7a] text-white px-5 py-2.5 text-sm font-medium transition-colors group"
            data-testid="navbar-cta-devis"
          >
            Devis gratuit
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#0F2B5B]"
            data-testid="navbar-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-white border-t border-slate-200 overflow-hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-slate-800 hover:text-[#2E9E6E] transition-colors"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-center gap-2 text-[#0F2B5B] font-medium"
              >
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
              <a
                href="#devis"
                onClick={() => setOpen(false)}
                className="bg-[#0F2B5B] text-white px-5 py-3 text-center text-sm font-medium"
              >
                Devis gratuit →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
